import React from 'react';
import { useDispatch } from 'react-redux';
import { type Column } from '@tanstack/react-table';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

import { useTranslation } from 'src/i18n';
import { pushHistory } from 'src/module/navigation/action';
import { cn } from 'src/component/ds/utils';
import { Badge } from 'src/component/ds/ui/badge';
import { Button } from 'src/component/ds/ui/button';
import { DataTableColumnHeader } from 'src/component/ds/ui/dataTableColumnHeader';
import { type DataTableFeatures } from 'src/component/ds/ui/dataTableFeatures';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/component/ds/ui/tooltip';
import {
  SEVERITY_ICON,
  SEVERITY_STYLE,
  type AlarmsToTriageSegment
} from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/severity';
import type { DeviceListLevels, DeviceListRow } from './deviceListRows';

function Hint ({ label, children }: { label: string; children: React.ReactElement }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function AlarmCountBadges ({ severities }: { severities: AlarmsToTriageSegment[] }) {
  return (
    <div className='ds-flex ds-gap-2'>
      {severities.map(({ severity, count, label }) => {
        const Icon = SEVERITY_ICON[severity];
        const fill = SEVERITY_STYLE[severity].bar;
        return (
          <Hint key={severity} label={label}>
            <Badge
              variant='outline'
              className={cn('ds-min-w-[39px] ds-justify-center ds-font-semibold', count && cn('ds-border-transparent ds-text-white', fill))}
            >
              <Icon aria-hidden='true' className={count ? undefined : 'ds-text-muted-foreground'} />
              <span className='ds-sr-only'>{label}: </span>
              {count}
            </Badge>
          </Hint>
        );
      })}
    </div>
  );
}

export const WorkOrdersCountBadge = ({ count }: { count: number }) => (
  <Badge variant={count ? 'secondary' : 'outline'} className='ds-min-w-[23px] ds-justify-center ds-font-semibold'>{count}</Badge>
);

function StatusIcons ({ offline, forcedOn }: { offline: boolean; forcedOn: boolean }) {
  const { t } = useTranslation();
  return (
    <div className='ds-flex ds-w-4 ds-shrink-0 ds-flex-col ds-items-center ds-gap-1'>
      <span
        role='img'
        aria-label={offline ? t('deviceList.offline') : t('deviceList.online')}
        className={cn('ds-size-[11px] ds-rounded-full', offline ? 'ds-bg-muted-foreground' : 'ds-bg-online')}
      />
      {forcedOn && (
        <Hint label={t('deviceList.forcedOn')}>
          <span
            role='img'
            aria-label={t('deviceList.forcedOn')}
            className='ds-flex ds-rounded-[2px] ds-border ds-border-forced-on ds-text-forced-on [&_svg]:ds-size-[10px]'
          >
            <PowerSettingsNewIcon aria-hidden='true' />
          </span>
        </Hint>
      )}
    </div>
  );
}

const LotoLock = () => {
  const { t } = useTranslation();
  return (
    <Hint label={t('deviceList.loto')}>
      <span role='img' aria-label={t('deviceList.loto')} className='ds-flex ds-shrink-0 ds-text-muted-foreground'>
        <LockOutlinedIcon aria-hidden='true' className='ds-size-4' />
      </span>
    </Hint>
  );
};

export function ControllerCell ({ data }: { data: DeviceListLevels['controller'] }) {
  if (!data.circuits) return null;
  return (
    <div className='ds-flex ds-items-center ds-gap-2'>
      <StatusIcons offline={data.offline} forcedOn={data.forcedOn} />
      <div className='ds-flex ds-flex-col'>
        <span className='ds-text-xs ds-font-semibold ds-underline'>{data.tag}</span>
        {data.model && <span className='ds-text-xs ds-font-normal ds-text-mist-400'>{data.model}</span>}
      </div>
      {data.loto && <LotoLock />}
    </div>
  );
}

export function CircuitCell ({ row }: { row: DeviceListRow }) {
  const { t } = useTranslation();

  if (row.level === 'controller' && row.data.circuits) {
    const { circuits } = row.data;
    let text = t('deviceList.circuitsTotal', { total: circuits.total });
    if (row.data.filtered) {
      text = t('deviceList.circuitsMatch', { shown: circuits.shown, total: circuits.total });
    } else if (circuits.shown !== circuits.total) {
      text = t('deviceList.circuitsShown', { shown: circuits.shown, total: circuits.total });
    }
    return <span className='ds-text-xs ds-font-medium ds-text-muted-foreground'>{text}</span>;
  }

  const { description, tag } = row.data;
  return (
    <div className='ds-flex ds-items-center ds-gap-2'>
      <StatusIcons offline={row.data.offline} forcedOn={row.data.forcedOn} />
      <div className='ds-flex ds-flex-col'>
        <span className='ds-text-xs ds-font-medium ds-underline'>{description || tag}</span>
        {description && <span className='ds-text-xs ds-font-medium ds-text-muted-foreground'>{tag}</span>}
      </div>
      {row.data.loto && <LotoLock />}
    </div>
  );
}

/* "View" sits beside the sort menu rather than inside it. */
export function AlarmsColumnHeader<TValue> ({ column }: { column: Column<DataTableFeatures, DeviceListRow, TValue> }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <div className='ds-flex ds-items-center ds-gap-2'>
      <DataTableColumnHeader column={column} title={t('deviceList.alarms')} />
      <Button variant='outline' size='xs' className='ds-gap-1 ds-rounded-full' onClick={() => dispatch(pushHistory('/alarms/active'))}>
        <NotificationsNoneOutlinedIcon aria-hidden='true' />
        {t('deviceList.view')}
        <ChevronRightIcon aria-hidden='true' className='ds-text-muted-foreground' />
      </Button>
    </div>
  );
}
